import React, { createContext, useContext } from 'react';
import { Accordion, AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger } from '@radix-ui/react-accordion';

import FacetAutoComplete from 'components/common/AutoComplete';
import { CheckboxFacet } from 'components/search/CheckboxFacet/CheckboxFacet';
import { RadioFacet } from 'components/search/RadioFacet/RadioFacet';
import { ButtonFacet } from 'components/search/ButtonFacet/ButtonFacet';
import {
  getFacetVariant,
  getSelectedFacetCount,
  getSelectedFacetNames,
  transformFacetValues
} from 'components/search/FacetAccordion/FacetAccordion.utils';
import { BRAND_FACET, REAL_BRAND_FACET } from 'components/search/FacetAccordion/FacetAccordion.constants';
import { cn } from 'helpers/classnames';
import type { AutoCompleteDataType, FacetDataType, FacetValueType } from 'types/search';
import type { FacetAccordionContextValue } from 'components/search/FacetAccordion/FacetAccordion.types';
import PlusSmallIcon from 'components/common/IconsV2/PlusSmallIcon';
import MinusSmallIcon from 'components/common/IconsV2/MinusSmallIcon';

const FacetAccordionContext = createContext<FacetAccordionContextValue | null>(null);

const useFacetAccordionContext = () => {
  const context = useContext(FacetAccordionContext);
  if (!context) {
    throw new Error('FacetAccordion compound components must be used within a FacetAccordion.Item');
  }
  return context;
};

const FacetAccordionRoot = Accordion;

type FacetAccordionItemProps = React.ComponentProps<typeof AccordionItem> & {
  facet: FacetDataType;
};

const FacetAccordionItem = ({ className, facet, ...props }: FacetAccordionItemProps) => {
  const contextValue: FacetAccordionContextValue = {
    facet
  };

  return (
    <FacetAccordionContext.Provider value={contextValue}>
      <AccordionItem
        className={cn('group/item flex flex-col gap-3 border-b border-b-gray-300 last:border-b-0 data-[state=open]:pb-4', className)}
        {...props}
      />
    </FacetAccordionContext.Provider>
  );
};

type FacetAccordionTriggerProps = React.ComponentProps<typeof AccordionTrigger>;

const FacetAccordionTrigger = ({ className, children, ...props }: FacetAccordionTriggerProps) => {
  const { facet } = useFacetAccordionContext();
  const selectedNames = getSelectedFacetNames(facet);

  return (
    <AccordionHeader>
      <AccordionTrigger
        className={cn(
          'relative flex w-full flex-1 appearance-none items-baseline gap-1 py-6 text-left data-[state=open]:pb-0',
          selectedNames && 'data-[state=closed]:pb-2',
          className
        )}
        {...props}
      >
        <div className="relative flex w-full min-w-0 flex-col items-start">{children}</div>

        <div className="shrink-0 text-gray-900">
          <PlusSmallIcon size={16} className="hidden group-data-[state=closed]/item:block" />
          <MinusSmallIcon size={16} className="hidden group-data-[state=open]/item:block" />
        </div>
      </AccordionTrigger>
    </AccordionHeader>
  );
};

type FacetAccordionDisplayNameProps = React.ComponentProps<'span'>;

const FacetAccordionDisplayName = ({ className, ...props }: FacetAccordionDisplayNameProps) => {
  const { facet } = useFacetAccordionContext();
  const { facetFieldDisplayName } = facet;
  const selectedCount = getSelectedFacetCount(facet);

  return (
    <span className={cn('body-m-medium', className)} {...props}>
      {facetFieldDisplayName}
      {selectedCount > 0 && ` (${selectedCount})`}
    </span>
  );
};

type FacetAccordionSelectedValueNamesProps = React.ComponentProps<'span'>;

const FacetAccordionSelectedValueNames = ({ className, ...props }: FacetAccordionSelectedValueNamesProps) => {
  const { facet } = useFacetAccordionContext();
  const selectedNames = getSelectedFacetNames(facet);

  return (
    <span
      className={cn(
        'body-xs w-full truncate text-gray-500 group-data-[state=open]/item:min-h-[1lh] group-data-[state=closed]:empty:hidden',
        className
      )}
      {...props}
    >
      {selectedNames}
    </span>
  );
};

type FacetAccordionContentProps = React.ComponentProps<typeof AccordionContent>;

const FacetAccordionContent = ({ className, ...props }: FacetAccordionContentProps) => (
  <AccordionContent className={cn('flex flex-col gap-2 empty:hidden', className)} {...props} />
);

type FacetAccordionAutoCompleteProps = {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const FacetAccordionAutoComplete = ({ onChange, ...props }: FacetAccordionAutoCompleteProps) => {
  const { facet } = useFacetAccordionContext();
  const { facetField, facetFieldDisplayName, values } = facet;

  const handleAutoComplete = (value: FacetValueType, searchText: string): boolean => {
    /**
     * Replace Umlaute (äöüÄÖÜ) characters with US characters (aouAOU)
     * For brands such as "Fjällräven"
     */
    const displayName = value.displayName
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036F]/g, '');
    return displayName.includes(searchText.toLowerCase());
  };

  const isBrandFacet = facetField === BRAND_FACET || facetField === REAL_BRAND_FACET;

  if (!isBrandFacet) {
    return null;
  }

  // Always use original values for autocomplete filtering, not filtered values
  return (
    <FacetAutoComplete id={facetField} title={facetFieldDisplayName} values={values} handler={handleAutoComplete} onChange={onChange} {...props} />
  );
};

type FacetAccordionValueListProps = Omit<React.ComponentProps<'ul'>, 'children'> & {
  autoComplete?: AutoCompleteDataType;
  children: ({ facetValue }: { facetValue: FacetValueType }) => React.ReactNode;
};

const FacetAccordionValueList = ({ autoComplete, className, children, ...props }: FacetAccordionValueListProps) => {
  const { facet } = useFacetAccordionContext();

  const variant = getFacetVariant(facet);
  const { groupedValues, sortedGroups } = transformFacetValues({ facet, autoComplete });

  const isBrandFacet = facet.facetField === BRAND_FACET || facet.facetField === REAL_BRAND_FACET;
  const ulClassName = variant === 'button' ? 'grid grid-cols-4 gap-2' : 'flex flex-col';

  return (
    <ul className={cn(ulClassName, isBrandFacet && 'max-h-[350px] overflow-y-auto', className)} {...props}>
      {sortedGroups.map(group => {
        const facetValues = groupedValues[group] ?? [];

        return (
          <React.Fragment key={group}>
            {group && (
              <li className="pt-1">
                <span className="body-s text-gray-600">{group}</span>
              </li>
            )}

            {facetValues.map(facetValue => (
              <React.Fragment key={facetValue.name}>{children({ facetValue })}</React.Fragment>
            ))}
          </React.Fragment>
        );
      })}
    </ul>
  );
};

type FacetAccordionValueProps = React.ComponentProps<'li'> & {
  facetValue: FacetValueType;
  onFacetSelect: () => void;
};

const FacetAccordionValue = ({ className, facetValue, onFacetSelect, ...props }: FacetAccordionValueProps) => {
  const { facet } = useFacetAccordionContext();
  const { displayName, count, selected, name } = facetValue;

  const variant = getFacetVariant(facet);

  switch (variant) {
    case 'radio':
      return (
        <li className={cn('py-2 first:pt-0 last:pb-0', className)} {...props}>
          <RadioFacet
            type="radio"
            label={displayName}
            count={(count ?? 0).toString()}
            checked={selected ?? false}
            name={facet.facetField}
            value={name}
            onClick={() => onFacetSelect()}
          />
        </li>
      );
    case 'button':
      return (
        <li className="last:pb-0" {...props}>
          <ButtonFacet
            type="button"
            label={displayName}
            count={(count ?? 0).toString()}
            isSelected={selected ?? false}
            onClick={() => onFacetSelect()}
          />
        </li>
      );
    case 'checkbox':
      return (
        <li className={cn('py-2 first:pt-0 last:pb-0', className)} {...props}>
          <CheckboxFacet type="checkbox" label={displayName} count={count.toString()} checked={selected ?? false} onClick={() => onFacetSelect()} />
        </li>
      );
  }
};

export const FacetAccordion = {
  Root: FacetAccordionRoot,
  Item: FacetAccordionItem,
  Trigger: FacetAccordionTrigger,
  DisplayName: FacetAccordionDisplayName,
  SelectedValueNames: FacetAccordionSelectedValueNames,
  Content: FacetAccordionContent,
  AutoComplete: FacetAccordionAutoComplete,
  ValueList: FacetAccordionValueList,
  Value: FacetAccordionValue
};
